import * as React from "react"
import { cva, type VariantProps } from "class-variance-authority"
import { Loader2 } from "lucide-react"
import { cn } from "@/lib/utils"
import { Slot } from "radix-ui"

/**
 * The one button system ("Minimal neutral", docs/design/buttons.md). Colours come from the --btn-* tokens in
 * index.css, so every route and both themes share it; pages pick a semantic variant and never restyle it.
 *
 *   default            graphite primary — the single main action of a view or step
 *   secondary          light neutral surface with a 3:1 border — alternatives (Save draft, Cancel, Filters)
 *   ghost              quiet text with a soft hover surface — tertiary actions (Back, Edit, menus' triggers)
 *   destructive        solid semantic red — confirming a destructive action
 *   destructive-ghost  red text — starting a destructive action (Discard draft, Turn off, Remove)
 *   link               underlined inline text action
 *
 * Sizes: xs 28 · sm 32 · default 36 · lg 40 (default and lg are 44 on phones) · icon 36 · icon-sm 32 · icon-xs 28.
 * `loading` keeps the variant's colours, shows a spinner in place of the leading icon and is announced as busy;
 * it blocks activation without looking disabled.
 */
const buttonVariants = cva(
  "inline-flex shrink-0 items-center justify-center gap-1.5 rounded-[var(--btn-radius)] border border-transparent font-medium whitespace-nowrap transition-[background-color,border-color,color,text-decoration-color] duration-100 focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-solid focus-visible:outline-btn-focus disabled:cursor-not-allowed aria-busy:cursor-progress [&_svg]:pointer-events-none [&_svg]:shrink-0 [&_svg:not([class*='size-'])]:size-4",
  {
    variants: {
      variant: {
        default:
          "bg-btn-primary text-btn-primary-fg hover:bg-btn-primary-hover active:bg-btn-primary-press disabled:bg-btn-disabled disabled:text-btn-disabled-fg",
        secondary:
          "border-btn-secondary-border bg-btn-secondary text-btn-secondary-fg hover:bg-btn-secondary-hover active:bg-btn-secondary-press disabled:border-btn-disabled-border disabled:bg-transparent disabled:text-btn-disabled-ink",
        ghost:
          "text-btn-ghost-fg hover:bg-btn-ghost-hover hover:text-foreground active:bg-btn-ghost-press disabled:bg-transparent disabled:text-btn-disabled-ink",
        destructive:
          "bg-btn-danger text-btn-danger-fg hover:bg-btn-danger-hover active:bg-btn-danger-press disabled:bg-btn-disabled disabled:text-btn-disabled-fg",
        "destructive-ghost":
          "text-btn-danger-ghost hover:bg-btn-danger-ghost-hover active:bg-btn-danger-ghost-press disabled:bg-transparent disabled:text-btn-disabled-ink",
        link:
          "text-foreground underline decoration-btn-secondary-border decoration-1 underline-offset-4 hover:decoration-current disabled:text-btn-disabled-ink disabled:no-underline",
      },
      size: {
        default: "h-9 px-3.5 text-base has-[>svg]:px-3 max-md:min-h-11",
        xs: "h-7 gap-1 px-2.5 text-sm [&_svg:not([class*='size-'])]:size-3.5",
        sm: "h-8 px-3 text-sm has-[>svg]:px-2.5",
        lg: "h-10 px-4 text-base has-[>svg]:px-3.5 max-md:min-h-11",
        icon: "size-9",
        "icon-sm": "size-8",
        "icon-xs": "size-7 [&_svg:not([class*='size-'])]:size-3.5",
      },
    },
    compoundVariants: [{ variant: "link", className: "h-auto min-h-0 p-0 has-[>svg]:px-0" }],
    defaultVariants: {
      variant: "default",
      size: "default",
    },
  }
)

function Button({
  className,
  variant = "default",
  size = "default",
  asChild = false,
  loading = false,
  disabled,
  onClick,
  children,
  ...props
}: React.ComponentProps<"button"> &
  VariantProps<typeof buttonVariants> & {
    asChild?: boolean
    /** Busy: keeps the variant's look, swaps the leading icon for a spinner, sets aria-busy and ignores activation. */
    loading?: boolean
  }) {
  const Comp = asChild ? Slot.Root : "button"
  const busy = loading && !asChild

  return (
    <Comp
      data-slot="button"
      data-variant={variant}
      data-size={size}
      data-loading={busy || undefined}
      aria-busy={busy || undefined}
      aria-disabled={busy || undefined}
      disabled={busy ? undefined : disabled}
      onClick={busy ? (e: React.MouseEvent<HTMLButtonElement>) => e.preventDefault() : onClick}
      className={cn(buttonVariants({ variant, size, className }), busy && "[&>svg:not(.btn-spinner)]:hidden")}
      {...props}
    >
      {busy ? (
        <>
          <Loader2 className="btn-spinner animate-spin" aria-hidden />
          {children}
        </>
      ) : (
        children
      )}
    </Comp>
  )
}

export { Button, buttonVariants }
